import { zonedDateKey } from '@/lib/format/date'

/**
 * The listing-detail history rail as data: which rows print, in what order,
 * and the dollar change each one prints.
 *
 * W6 (Muse audit 2026-10-20). 3335 NW Fairway Heights printed a price-drop
 * banner of "$46K" off $1,645,000 while the rail under it printed "$50K down"
 * and a same-day "+$4K" row. The banner read `price_change` off one row and
 * the rail took the difference between adjacent rows, so the two could name
 * different cuts from the same history. Now the rail is built here, once, and
 * the banner (publishListingDropMark) and its label (publishListingLastDrop)
 * read the delta off the rail row, so the banner amount IS the rail's delta.
 *
 * Section 0: every figure is a row price or the difference of two row prices
 * already on the rail; `price_change` is only the fallback when the rail holds
 * no earlier price to measure from.
 */
/**
 * The price a SALE row in the history rail publishes (SITE-21).
 *
 * FOUNDING CASE, measured on a dev render 2026-09-09 of Closed MLS 220224752:
 * the sold instrument read "Sold for $827,000 on September 8, 2026" while the
 * history rail further down the same page read "Sep 8, 2026 · Sold ·
 * $849,000". Two prices for one sale on one page, which is the §0 failure this
 * node exists to end. The rail was not wrong about its own row — the MLS
 * change-log event that flips the status to Closed carries the then-current
 * LIST price — but a row LABELLED Sold publishes a sale, and the price of a
 * sale is the ClosePrice.
 *
 * So a sale row publishes the close price or NO price. There is no fallback to
 * the ask, for the same reason publish-listing-published-price has none: the
 * list price of a home that already sold, printed under the word Sold, IS the
 * defect. A row with no publishable price renders a dash; the ask is still
 * visible on its own Listed and Price change rows, where it is true.
 */
export function publishHistoryRowPrice(input: {
  /** True when this row's published LABEL is the sale ("Sold"). */
  isSale: boolean
  /** The price the merged history row carries. */
  rowPrice: number | null | undefined
  /** ClosePrice from the listing row, or null when the feed withholds it. */
  closePrice: number | null | undefined
}): number | null {
  if (!input.isSale) return input.rowPrice ?? null
  const close = input.closePrice
  return close != null && Number.isFinite(close) && close > 0 ? close : null
}

export type HistoryRailInputRow = {
  event?: string | null
  event_date?: string | null
  price?: number | null
  price_change?: number | null
  description?: string | null
}

export type HistoryRailRow = {
  event: string | undefined
  event_date: string | null | undefined
  description: string | null | undefined
  /** The label the rail prints ("Listed", "Price change", "Sold", ...). */
  label: string
  /** The row's published price (a sale row publishes the close price). */
  price: number | null
  /** The dollar change this row prints: negative is a cut. Null prints none. */
  delta: number | null
  /** A price action (change / drop / increase), as opposed to a status row. */
  isPriceEvent: boolean
}

function normalizeEvent(raw: string | null | undefined): string {
  return (raw ?? '').toLowerCase().replace(/[\s_-]+/g, '')
}

export const HISTORY_EVENT_LABEL: Record<string, string> = {
  newlisting: 'Listed',
  listed: 'Listed',
  pricechange: 'Price change',
  pricedrop: 'Price drop',
  priceincrease: 'Price increase',
  statuschange: 'Status change',
  pending: 'Pending',
  statuspending: 'Pending',
  active: 'Active',
  statusactive: 'Active',
  backonmarket: 'Back on market',
  contingent: 'Contingent',
  statuscontingent: 'Contingent',
  closed: 'Sold',
  sold: 'Sold',
  statusclosed: 'Sold',
  expired: 'Expired',
  statusexpired: 'Expired',
  canceled: 'Canceled',
  cancelled: 'Canceled',
  statuscanceled: 'Canceled',
  withdrawn: 'Withdrawn',
  statuswithdrawn: 'Withdrawn',
}

const PRICE_EVENTS = new Set(['pricechange', 'pricedrop', 'priceincrease'])
/** Rows whose price is an ask the seller set: a price action is measured from one. */
const PRICE_ANCHOR_EVENTS = new Set(['listed', 'newlisting', ...PRICE_EVENTS])

export function historyEventLabel(raw: string | null | undefined): string {
  const key = normalizeEvent(raw)
  if (!key) return 'Update'
  return HISTORY_EVENT_LABEL[key] ?? raw!.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Only price + status moments belong in the buyer-facing history. MLS change-log
// noise (document uploads, photo swaps, text/field edits, tour/open-house pings)
// is filtered out — that wall of "DOCUMENT / PHOTO / TEXTCHANGE" rows is not history.
function isMeaningfulEvent(ev: HistoryRailInputRow): boolean {
  if (HISTORY_EVENT_LABEL[normalizeEvent(ev.event)]) return true
  // A generic/unknown change still counts when it actually moved the price.
  if (ev.price_change && ev.price_change !== 0) return true
  return false
}

function railDay(iso: string | null | undefined): string | null {
  const raw = iso?.trim()
  if (!raw) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
  return zonedDateKey(raw) || null
}

/** 2: says which way the ask moved (or the rail names it by its delta). 1: "Price change". 0: not a price row. */
function priceRowRank(ev: HistoryRailInputRow): number {
  const key = normalizeEvent(ev.event)
  if (key === 'pricedrop' || key === 'priceincrease') return 2
  if (key === 'pricechange') return 1
  if (!HISTORY_EVENT_LABEL[key] && ev.price_change != null && ev.price_change !== 0) return 2
  return 0
}

/**
 * A stored change in dollars. Spark's change-log PriceChange is a fraction of
 * the ask (-0.015), which printed as a dollar figure would be "$0 down".
 */
function dollarChange(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) && Math.abs(value) >= 1 ? value : null
}

/** The rail event a winning row keeps: an unlabelled change-log row is named by its sign. */
function priceRowEvent(ev: HistoryRailInputRow): string | null | undefined {
  if (HISTORY_EVENT_LABEL[normalizeEvent(ev.event)]) return ev.event
  return (ev.price_change ?? 0) < 0 ? 'pricedrop' : 'priceincrease'
}

/**
 * One price row per Pacific day and resulting price (W26, 2026-10-21). 3335 NW
 * Fairway Heights printed every cut twice, "Price change" and "Price drop",
 * because two sources described the same move. The more specific label wins;
 * the first stored dollar change is kept. Rows with no price are left alone.
 */
export function dedupeRailPriceRows<T extends HistoryRailInputRow>(rows: ReadonlyArray<T>): T[] {
  const out: T[] = []
  const at = new Map<string, number>()
  for (const row of rows) {
    const rank = priceRowRank(row)
    const day = railDay(row.event_date)
    const price = row.price != null && Number.isFinite(row.price) && row.price > 0 ? Math.round(row.price) : null
    if (rank === 0 || day == null || price == null) {
      out.push(row)
      continue
    }
    const key = `${day}|${price}`
    const i = at.get(key)
    if (i == null) {
      at.set(key, out.length)
      out.push(row)
      continue
    }
    const kept = out[i]!
    out[i] = {
      ...kept,
      event: priceRowEvent(rank > priceRowRank(kept) ? row : kept),
      price_change: dollarChange(kept.price_change) ?? dollarChange(row.price_change) ?? kept.price_change ?? null,
    }
  }
  return out
}

function dateMs(iso: string | null | undefined): number {
  if (!iso) return 0
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : 0
}

/** The rail, newest first, with the rows that print nothing already dropped. */
export function publishHistoryRail(
  history: ReadonlyArray<HistoryRailInputRow> | null | undefined,
  options: { mode?: 'all' | 'meaningful-only'; closePrice?: number | null } = {},
): HistoryRailRow[] {
  const mode = options.mode ?? 'all'
  const closePrice = options.closePrice ?? null
  const input = history ?? []
  const filtered = dedupeRailPriceRows(mode === 'meaningful-only' ? input.filter(isMeaningfulEvent) : input)
  // Newest first. The merged history arrives oldest first, so on a tie (two rows
  // on one day) the later row in the input is the newer one.
  const sorted = filtered
    .map((ev, i) => ({ ev, i }))
    .sort((a, b) => dateMs(b.ev.event_date) - dateMs(a.ev.event_date) || b.i - a.i)
    .map(({ ev }) => ev)
  // Republish every row's price BEFORE the deltas are taken off it, so the
  // dollar change on a sale row is measured against the sale and not against
  // the ask the change-log happened to be carrying.
  const events = sorted.map((ev) => ({
    ...ev,
    norm: normalizeEvent(ev.event),
    label: historyEventLabel(ev.event),
    price: publishHistoryRowPrice({
      isSale: historyEventLabel(ev.event) === 'Sold',
      rowPrice: ev.price,
      closePrice,
    }),
  }))

  const out: HistoryRailRow[] = []
  events.forEach((ev, i) => {
    const isPriceEvent = PRICE_EVENTS.has(ev.norm)
    const older = events.slice(i + 1)
    // A price action is measured from the last ask the seller set (a listed or
    // price row). A status row's price is the ask in effect, and the
    // difference against the row before it is what the rail always printed.
    const prevPrice =
      (isPriceEvent
        ? older.find((e) => e.price != null && PRICE_ANCHOR_EVENTS.has(e.norm))
        : older.find((e) => e.price != null)
      )?.price ?? null
    const storedDelta = dollarChange(ev.price_change)
    const measured = ev.price != null && prevPrice != null ? ev.price - prevPrice : null
    if (isPriceEvent && (measured == null || measured === 0) && storedDelta == null) return
    let label = ev.label
    const delta = measured ?? (isPriceEvent ? storedDelta : null)
    if (!HISTORY_EVENT_LABEL[ev.norm]) {
      if (delta && delta < 0) label = 'Price drop'
      else if (delta && delta > 0) label = 'Price increase'
      else if (ev.price != null && (delta == null || delta === 0)) return
    }
    out.push({
      event: ev.event ?? undefined,
      event_date: ev.event_date,
      description: ev.description,
      label,
      price: ev.price,
      delta: delta && delta !== 0 ? delta : null,
      isPriceEvent: isPriceEvent || label === 'Price drop' || label === 'Price increase',
    })
  })
  return out
}

/**
 * The newest price action on the rail since the home was last listed, as the
 * rail prints it. Null when there is none, or when the newest action is a rise:
 * a cut the seller has since undone is not a current cut (2026-09-11).
 */
export function newestRailCut(
  rows: ReadonlyArray<HistoryRailInputRow> | null | undefined,
): HistoryRailRow | null {
  const rail = publishHistoryRail(rows, { mode: 'meaningful-only' })
  const listedCutoff =
    rail
      .filter((row) => row.event_date && (normalizeEvent(row.event) === 'listed' || normalizeEvent(row.event) === 'newlisting'))
      .map((row) => row.event_date!)
      .sort()
      .at(-1) ?? null
  for (const row of rail) {
    if (!row.event_date) continue
    if (listedCutoff && row.event_date < listedCutoff) continue
    if (!row.isPriceEvent || row.delta == null) continue
    return row.delta < 0 ? row : null
  }
  return null
}
